import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { num, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Mileage() {
  const { data: rows, loading, refetch } = useFetch("/mileage");
  const { data: vans } = useFetch("/vans");
  const { data: drivers } = useFetch("/drivers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ van_reg: "", driver_id: "", date: todayISO(), start_mileage: "", end_mileage: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const total = (parseFloat(form.end_mileage) || 0) - (parseFloat(form.start_mileage) || 0);

  const save = async () => {
    try {
      await api.post("/mileage", { ...form });
      toast.success("Mileage logged");
      setOpen(false);
      setForm({ van_reg: "", driver_id: "", date: todayISO(), start_mileage: "", end_mileage: "" });
      refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/mileage/${id}`); refetch(); } catch (e) { toast.error("Error"); } };

  if (loading) return <Loader />;

  return (
    <div className="fp-fade-in">
      <PageHeader title="Mileage" subtitle="Daily start/end odometer per van" testId="mileage-page">
        <Button onClick={() => setOpen(true)} className="w-full sm:w-auto" data-testid="add-mileage-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Log Mileage</Button>
      </PageHeader>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Van</TableHead><TableHead>Driver</TableHead>
              <TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Total Miles</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(rows || []).map((r) => (
                <TableRow key={r.id} data-testid={`mileage-row-${r.id}`}>
                  <TableCell className="font-mono text-sm">{ukDate(r.date)}</TableCell>
                  <TableCell><Plate reg={r.van_reg} /></TableCell>
                  <TableCell>{r.driver_name || "—"}</TableCell>
                  <TableCell className="font-mono">{num(r.start_mileage)}</TableCell>
                  <TableCell className="font-mono">{num(r.end_mileage)}</TableCell>
                  <TableCell className="font-mono font-semibold text-primary">{num(r.total_miles)} mi</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(!rows || rows.length === 0) && <EmptyState icon="Gauge" title="No mileage records" />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mileage-dialog">
          <DialogHeader><DialogTitle>Log Mileage</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Van</Label>
              <Select value={form.van_reg} onValueChange={set("van_reg")}>
                <SelectTrigger className="mt-1.5" data-testid="mileage-van-select"><SelectValue placeholder="Select van" /></SelectTrigger>
                <SelectContent>{(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver</Label>
              <Select value={form.driver_id || "none"} onValueChange={(v) => set("driver_id")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" /></div>
            <div />
            <div><Label>Start Mileage</Label><Input type="number" value={form.start_mileage} onChange={set("start_mileage")} className="mt-1.5" data-testid="mileage-start-input" /></div>
            <div><Label>End Mileage</Label><Input type="number" value={form.end_mileage} onChange={set("end_mileage")} className="mt-1.5" data-testid="mileage-end-input" /></div>
          </div>
          <div className="rounded-lg bg-muted p-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total miles driven</span>
            <span className="font-mono font-bold text-lg text-primary">{num(total > 0 ? total : 0)} mi</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-mileage-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
